import * as React from 'react';
import { useEffect, useState } from 'react';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import {
  getSchoolAwardTypes,
  grantAward,
  getAwardEventLogs,
  AwardType,
  AwardEventInput,
  AwardLogEntry
} from 'Src/helpers/service/admin/awards';
import { AppUser } from 'Src/views/App/App';
import { SchoolStudent } from 'Src/models/schoolStudent';
import { SchoolHouse } from 'Src/models/house';
import {
  getAllAwardEventPhotos,
  addAwardEventPhoto,
  deleteAwardEventPhoto
} from 'Src/helpers/service/admin/awardEventPhotos';
import {
  getAllAwardEventVideos,
  addAwardEventVideo,
  deleteAwardEventVideo
} from 'Src/helpers/service/admin/awardEventVideos';
import { uploadImage } from 'Src/helpers/service/image';
import { getSafeFileName } from 'Src/helpers/file/file';
import { getKey } from 'Src/helpers/service/nobody/key';
import { getVideoConfig } from 'Src/helpers/service/nobody/config';
import { addConvertJob } from 'Src/helpers/service/nobody/aws';
import { ACCESS_PRESET } from 'Src/consts/album';
import S3 = require('aws-sdk/clients/s3');
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import './GrantAwardModal.scss';

export type StudentLite = Pick<SchoolStudent, 'id' | 'permissionId' | 'firstName' | 'lastName'> & {
  houseId?: string | null;
  house?: { id?: string | null } | null;
};

interface Props {
  isOpen: boolean;
  onClose: (result?: { granted?: boolean }) => void;
  students?: StudentLite[];
  houses?: SchoolHouse[];
  user: AppUser;
  preselectedType?: AwardType | null;
  eventId?: string;
}

type Step = 'select' | 'details' | 'confirm-missing-houses';

const AwardTypeSelector: React.FC<{
  types: AwardType[];
  onSelect: (type: AwardType) => void;
  loading: boolean;
  error?: string;
}> = ({ types, onSelect, loading, error }) => {
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);

  const FAV_KEY = 'awardType:favorites';
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(FAV_KEY);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });
  const toggleFav = (id: string) => {
    setFavorites(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      try {
        localStorage.setItem(FAV_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const isHouse = (t: AwardType) =>
    Boolean((t as any).isSystemHidden) || Number((t as any).defaultHousePoints || 0) !== 0 || /house/i.test(t.name);

  const filtered = types
    .filter(t => (appliedSearch ? t.name.toLowerCase().includes(appliedSearch) : true))
    .sort((a, b) => {
      const af = favorites.includes(a.id) ? 1 : 0;
      const bf = favorites.includes(b.id) ? 1 : 0;
      if (af !== bf) return bf - af;
      return a.name.localeCompare(b.name);
    });

  const PAGE_SIZE = 24;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageClamped = Math.min(page, totalPages);
  const visible = filtered.slice(0, pageClamped * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [appliedSearch]);

  return (
    <div className="grantAwardTypePicker">
      <div className="grantAwardTypePicker__toolbar">
        <input
          type="search"
          className="grantAwardTypePicker__search"
          placeholder="Search award types"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setAppliedSearch(search.trim().toLowerCase());
            }
          }}
        />
        <span className="grantAwardTypePicker__count">
          {filtered.length} {filtered.length === 1 ? 'award type' : 'award types'}
        </span>
      </div>

      {loading ? (
        <div className="grantAwardTypePicker__state" aria-live="polite">
          <span className="grantAwardTypePicker__spinner" aria-hidden="true" />
          Loading award types…
        </div>
      ) : error ? (
        <div className="grantAwardTypePicker__state is-error">{error}</div>
      ) : visible.length ? (
        <div className="grantAwardTypePicker__grid">
          {visible.map(t => {
            const fav = favorites.includes(t.id);
            const hp = Number((t as any).defaultHousePoints || 0);
            const housey = isHouse(t);
            return (
              <div
                key={t.id}
                role="button"
                tabIndex={0}
                className="grantAwardTypeCard"
                onClick={() => onSelect(t)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(t);
                  }
                }}
              >
                <span className="grantAwardTypeCard__visual">
                  {t.picture ? <img src={t.picture} alt="" /> : <span aria-hidden="true">★</span>}
                </span>
                <span className="grantAwardTypeCard__body">
                  <strong title={t.name}>{t.name}</strong>
                  <span>{housey ? `${Number.isFinite(hp) ? hp : 0} house points` : 'Student award'}</span>
                </span>
                {housey ? <span className="grantAwardTypeCard__points">{Number.isFinite(hp) ? hp : 0} pts</span> : null}
                <button
                  type="button"
                  className={`grantAwardTypeCard__favourite ${fav ? 'is-active' : ''}`}
                  aria-label={fav ? `Remove ${t.name} from favourites` : `Add ${t.name} to favourites`}
                  aria-pressed={fav}
                  title={fav ? 'Remove from favourites' : 'Add to favourites'}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    toggleFav(t.id);
                  }}
                >
                  {fav ? '★' : '☆'}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grantAwardTypePicker__state">
          <span className="grantAwardTypePicker__emptyIcon" aria-hidden="true">
            ★
          </span>
          <strong>No award types found</strong>
          <span>Try a different search term.</span>
        </div>
      )}

      {!loading && !error && filtered.length > visible.length && (
        <div className="grantAwardTypePicker__more">
          <button type="button" onClick={() => setPage(p => p + 1)}>
            Show {Math.min(PAGE_SIZE, filtered.length - visible.length)} more
          </button>
        </div>
      )}
    </div>
  );
};

export const GrantAwardModal: React.FC<Props> = ({
  isOpen,
  onClose,
  students = [],
  houses = [],
  user,
  preselectedType,
  eventId
}) => {
  const isHouseMode = houses.length > 0;
  const [types, setTypes] = useState<AwardType[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typesError, setTypesError] = useState('');
  const [selectedType, setSelectedType] = useState<AwardType | null>(null);
  const [step, setStep] = useState<Step>('select');

  const [awardedAt, setAwardedAt] = useState<string>(new Date().toISOString().slice(0, 10));
  const [points, setPoints] = useState<number>(0);
  const [reason, setReason] = useState('');
  const [awardedByName, setAwardedByName] = useState('');

  const [newAwardId, setNewAwardId] = useState<string | null>(null);
  const [logs, setLogs] = useState<AwardLogEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState<{ points?: string; form?: string }>({});

  const [createdAwardIds, setCreatedAwardIds] = useState<string[]>([]);

  const [photos, setPhotos] = useState<any[]>([]);
  const [videos, setVideos] = useState<any[]>([]);
  const photoInputRef = React.useRef<HTMLInputElement>(null);
  const videoInputRef = React.useRef<HTMLInputElement>(null);

  const [pendingWithHouse, setPendingWithHouse] = useState<StudentLite[] | null>(null);
  const [pendingNoHouse, setPendingNoHouse] = useState<StudentLite[] | null>(null);

  const getHouseId = (s: any): string | null => (s?.houseId ?? s?.house?.id ?? null) || null;

  const getEffectiveHousePoints = (t?: AwardType | null): number => Number(t?.defaultHousePoints || 0);

  const isoDay = (d: string) => new Date(d).toISOString();

  const isPreselectedFlow = !!preselectedType;

  const buildHousePayload = (h: SchoolHouse) => ({
    awardTypeId: selectedType!.id,
    awardedAt: isoDay(awardedAt),
    awardedByName: (
      awardedByName ||
      `${user.profile.firstName ?? ''} ${user.profile.lastName ?? ''}` ||
      'Unknown'
    ).trim(),
    points,
    reason: reason || undefined,
    houseId: h.id
  });

  const clearPending = () => {
    setPendingWithHouse(null);
    setPendingNoHouse(null);
  };

  useEffect(() => {
    if (!isOpen) return;

    setErrors({});
    setNewAwardId(null);
    setCreatedAwardIds([]);
    setLogs([]);
    setPhotos([]);
    setVideos([]);
    setReason('');
    setAwardedByName('');
    setAwardedAt(new Date().toISOString().slice(0, 10));
    setTypesError('');
    clearPending();

    if (preselectedType) {
      setTypesLoading(false);
      setSelectedType(preselectedType);
      setPoints(preselectedType.defaultHousePoints);
      setStep('details');
    } else {
      setStep('select');
      setSelectedType(null);
      setTypes([]);
      setTypesLoading(true);

      const role: string = user.activeRole;
      getSchoolAwardTypes(user, {
        where: { allowedRoles: role, isEnabled: true },
        limit: 1000
      }).then(
        fetched => {
          setTypes(fetched);
          setTypesLoading(false);
        },
        err => {
          console.error('Failed loading award types', err);
          setTypesError('Award types could not be loaded. Close this window and try again.');
          setTypesLoading(false);
        }
      );
    }
  }, [isOpen, user, preselectedType]);

  const validate = () => {
    const next: { points?: string } = {};
    if (selectedType?.isSystemHidden && Number(points) === 0) {
      next.points = 'House-points awards must use a non-zero point value.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async () => {
    if (loading || !selectedType) return;
    if (!validate()) return;

    setLoading(true);
    setErrors({});

    const defaultNominator =
      user.profile.firstName && user.profile.lastName
        ? `${user.profile.firstName} ${user.profile.lastName}`
        : 'Unknown';
    const nominaterName = (awardedByName || '').trim() || defaultNominator;
    const hpPerAward = getEffectiveHousePoints(selectedType);

    const buildStudentPayload = (s: StudentLite, zeroHP: boolean): AwardEventInput => {
      const hid = getHouseId(s);
      const payload: any = {
        awardTypeId: selectedType!.id,
        awardedAt: isoDay(awardedAt),
        awardedByName: nominaterName,
        points,
        eventId: eventId || undefined,
        reason: reason || undefined,
        studentData: [{ userId: s.id, permissionId: s.permissionId }],
        ...(hid ? { houseId: hid } : {})
      };
      if (zeroHP) payload.housePointsOverride = 0;
      return payload as AwardEventInput;
    };
    const createStudentBatch = async (list: StudentLite[], zeroHP: boolean) => {
      const inputs = list.map(s => buildStudentPayload(s, zeroHP));
      const results = await Promise.all(inputs.map(input => grantAward(user, input as any)));
      const flattened = results.reduce((acc, arr) => acc.concat(arr), [] as { id: string }[]);
      return flattened.map(a => a.id as string);
    };
    const createHouseBatch = async (list: SchoolHouse[]) => {
      const inputs = list.map(h => buildHousePayload(h));
      const results = await Promise.all(inputs.map(input => grantAward(user, input as any)));
      const flattened = results.reduce((acc, arr) => acc.concat(arr), [] as { id: string }[]);
      return flattened.map(a => a.id as string);
    };

    try {
      let createdIds: string[] = [];

      if (isHouseMode) {
        if (selectedType.isSystemHidden && Number(points) === 0) {
          throw new Error('House-points awards must use a non-zero point value.');
        }
        createdIds = await createHouseBatch(houses);
      } else {
        const target = [...students];
        const withHouse = target.filter(s => getHouseId(s));
        const noHouse = target.filter(s => !getHouseId(s));

        if (hpPerAward !== 0 && (withHouse.length === 0 || noHouse.length > 0)) {
          setPendingWithHouse(withHouse);
          setPendingNoHouse(noHouse);
          setStep('confirm-missing-houses');
          setLoading(false);
          return;
        }
        createdIds = await createStudentBatch(target, false);
      }

      setCreatedAwardIds(createdIds);
      const lastId = createdIds[createdIds.length - 1] || null;
      setNewAwardId(lastId);

      if (lastId) {
        const fetchedLogs = await getAwardEventLogs(user, lastId);
        setLogs(fetchedLogs);
      }
      if (createdIds[0]) {
        const [ph, vd] = await Promise.all([
          getAllAwardEventPhotos(user, createdAwardIds[0] ?? createdIds[0]),
          getAllAwardEventVideos(user, createdAwardIds[0] ?? createdIds[0])
        ]);
        setPhotos(ph);
        setVideos(vd);
      }
    } catch (err) {
      const e = err as any;
      const msg = e?.response?.data?.details?.text || e?.response?.data?.error || e?.message || 'Something went wrong';

      if (/non[-\s]?zero/i.test(msg) || /house[-\s]?points.*must/i.test(msg)) {
        setErrors({ points: msg });
      } else {
        setErrors({ form: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  const onConfirmMissingHouses = async () => {
    if (isHouseMode) {
      setStep('details');
      return;
    }
    if (!pendingWithHouse && !pendingNoHouse) {
      setStep('details');
      return;
    }
    setLoading(true);

    try {
      const displayName =
        (awardedByName || '').trim() ||
        (user.profile.firstName && user.profile.lastName
          ? `${user.profile.firstName} ${user.profile.lastName}`
          : 'Unknown');

      const requestsWithHouse =
        pendingWithHouse && pendingWithHouse.length
          ? pendingWithHouse.map(s =>
              grantAward(user, {
                awardTypeId: selectedType!.id,
                awardedAt: new Date(awardedAt).toISOString(),
                awardedByName: displayName,
                points,
                reason: reason || undefined,
                studentData: [{ userId: s.id, permissionId: s.permissionId }],
                houseId: getHouseId(s) || undefined,
                eventId: eventId || undefined
              } as any)
            )
          : [];

      const requestsNoHouse =
        pendingNoHouse && pendingNoHouse.length
          ? pendingNoHouse.map(s =>
              grantAward(user, {
                awardTypeId: selectedType!.id,
                awardedAt: new Date(awardedAt).toISOString(),
                awardedByName: displayName,
                points,
                reason: reason || undefined,
                studentData: [{ userId: s.id, permissionId: s.permissionId }],
                housePointsOverride: 0,
                eventId: eventId || undefined
              } as any)
            )
          : [];

      const [resWith, resNo] = await Promise.all([Promise.all(requestsWithHouse), Promise.all(requestsNoHouse)]);

      const createdIds: string[] = []
        .concat(...resWith.map(arr => arr.map(a => a.id)))
        .concat(...resNo.map(arr => arr.map(a => a.id)));

      setCreatedAwardIds(createdIds);
      const lastId = createdIds.length ? createdIds[createdIds.length - 1] : null;
      setNewAwardId(lastId);

      if (lastId) {
        const fetchedLogs = await getAwardEventLogs(user, lastId);
        setLogs(fetchedLogs);
      }
      if (createdIds[0]) {
        const [ph, vd] = await Promise.all([
          getAllAwardEventPhotos(user, createdIds[0]),
          getAllAwardEventVideos(user, createdIds[0])
        ]);
        setPhotos(ph);
        setVideos(vd);
      }
    } catch (e) {
      const msg = (e as any)?.response?.data?.error || (e as any)?.message || 'Something went wrong';
      setErrors({ form: msg });
    } finally {
      setLoading(false);
      clearPending();
      setStep('details');
    }
  };

  const onCancelMissingHouses = () => {
    clearPending();
    setStep('details');
  };

  const handleClose = () => {
    const wasGranted = Boolean(newAwardId);

    if (wasGranted) {
      onClose({ granted: true });
      return;
    }

    if (step === 'details') {
      if (isPreselectedFlow) {
        onClose({ granted: false });
      } else {
        setStep('select');
        setErrors({});
      }
      return;
    }

    onClose({ granted: false });
  };

  const onAddPhotoClick = () => photoInputRef.current?.click();
  const onAddVideoClick = () => videoInputRef.current?.click();

  const onPhotoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || createdAwardIds.length === 0) return;

    setLoading(true);
    try {
      const accessPreset = user.activeSchool?.photoAccessPreset ?? ACCESS_PRESET.PUBLIC;

      const up = await uploadImage(user, file);
      const apiImg = (window as any)?.apiImg;
      const picUrl =
        up && typeof up === 'object' && 'key' in up && (up as any).key && apiImg
          ? `${apiImg}/images/${(up as any).key}`
          : (up as any)?.picUrl ||
            (up as any)?.url ||
            (up as any)?.secure_url ||
            (up as any)?.Location ||
            (up as any)?.location ||
            '';

      if (!picUrl) throw new Error('uploadImage did not return a usable URL/key');

      const payload = {
        picUrl,
        originalFileName: file.name,
        accessPreset,
        isPublished: true,
        isApproved: true,
        visibleForRoles: ['STAFF', 'PARENT', 'STUDENT']
      };

      await Promise.all(createdAwardIds.map(aid => addAwardEventPhoto(user, aid, payload)));

      if (createdAwardIds[0]) {
        const ph = await getAllAwardEventPhotos(user, createdAwardIds[0]);
        setPhotos(ph);
      }
    } catch (err) {
      console.error(err);
      setErrors({ form: 'The photo could not be uploaded. Please try again.' });
    } finally {
      setLoading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const onVideoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || createdAwardIds.length === 0) return;

    setLoading(true);
    try {
      const safeName = getSafeFileName(file.name);
      const [videoConfig, keyObj] = await Promise.all([getVideoConfig(user), getKey(user)]);
      const { accessKey, secretAccessKey, bucketRegion, bucketName } = videoConfig;

      const s3 = new S3({
        region: bucketRegion,
        credentials: { accessKeyId: accessKey, secretAccessKey }
      });

      const folderKey = keyObj.key;
      const folderName = `orig/${folderKey}/`;
      const videoFullName = folderName + safeName;

      await s3.putObject({ Key: folderName, Bucket: bucketName }).promise();
      await s3.putObject({ Key: videoFullName, Bucket: bucketName, Body: file, ContentType: file.type }).promise();

      const convert = await addConvertJob(user, { key: folderKey, fileName: safeName, bucketName });
      const jobId = convert?.Job?.Id || convert?.jobId || convert?.id;
      const accessPreset = user.activeSchool?.photoAccessPreset ?? ACCESS_PRESET.PUBLIC;

      await Promise.all(
        createdAwardIds.map(aid =>
          addAwardEventVideo(user, aid, { fileName: safeName, key: folderKey, jobId, accessPreset })
        )
      );

      if (createdAwardIds[0]) {
        const vd = await getAllAwardEventVideos(user, createdAwardIds[0]);
        setVideos(vd);
      }
    } catch (err) {
      console.error(err);
      setErrors({ form: 'The video could not be uploaded. Please try again.' });
    } finally {
      setLoading(false);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const onDeletePhoto = async (photoId: string) => {
    if (!createdAwardIds[0]) return;
    setLoading(true);
    try {
      await deleteAwardEventPhoto(user, createdAwardIds[0], photoId);
      const ph = await getAllAwardEventPhotos(user, createdAwardIds[0]);
      setPhotos(ph);
    } finally {
      setLoading(false);
    }
  };

  const onDeleteVideo = async (videoId: string) => {
    if (!createdAwardIds[0]) return;
    setLoading(true);
    try {
      await deleteAwardEventVideo(user, createdAwardIds[0], videoId);
      const vd = await getAllAwardEventVideos(user, createdAwardIds[0]);
      setVideos(vd);
    } finally {
      setLoading(false);
    }
  };

  const recipientCount = isHouseMode ? houses.length : students.length;
  const recipientLabel = isHouseMode
    ? `${recipientCount} ${recipientCount === 1 ? 'house' : 'houses'}`
    : `${recipientCount} ${recipientCount === 1 ? 'student' : 'students'}`;
  const modalState = createdAwardIds.length ? 'success' : step === 'confirm-missing-houses' ? 'warning' : step;
  const modalHeading = createdAwardIds.length
    ? 'Award granted'
    : step === 'select'
    ? 'Select award type'
    : step === 'confirm-missing-houses'
    ? 'Students without a house'
    : `Grant ${selectedType?.name || 'award'}`;
  const modalWidth = step === 'select' && !createdAwardIds.length ? 820 : createdAwardIds.length ? 680 : 590;
  const profileName = `${user.profile.firstName || ''} ${user.profile.lastName || ''}`.trim();
  const directClose = () => onClose({ granted: createdAwardIds.length > 0 });
  const onAwardDateChange = (value: Date | null) => {
    if (!value || Number.isNaN(value.getTime())) return;
    const year = value.getFullYear();
    const month = `0${value.getMonth() + 1}`.slice(-2);
    const day = `0${value.getDate()}`.slice(-2);
    setAwardedAt(`${year}-${month}-${day}`);
  };

  const modalTitle = (
    <span className="grantAwardHeader">
      <span className={`grantAwardHeader__icon is-${modalState}`} aria-hidden="true">
        {modalState === 'success' ? '✓' : modalState === 'warning' ? '!' : '★'}
      </span>
      <span className="grantAwardHeader__copy">
        <small>Awards</small>
        <strong>{modalHeading}</strong>
        <em>{recipientLabel}</em>
      </span>
    </span>
  );

  return (
    <SimpleModal
      isOpen={isOpen}
      customClass={`grant-award-modal grant-award-modal--${modalState}`}
      dialogStyle={{ maxWidth: modalWidth }}
      title={modalTitle}
      isBodyBold={false}
      showFooter={step !== 'select' || createdAwardIds.length > 0}
      buttonText={
        createdAwardIds.length
          ? undefined
          : step === 'select'
          ? undefined
          : step === 'confirm-missing-houses'
          ? loading
            ? 'Granting…'
            : 'Continue'
          : loading
          ? 'Saving…'
          : 'Grant'
      }
      buttonCancelText={
        createdAwardIds.length
          ? 'Close'
          : step === 'confirm-missing-houses'
          ? 'Back'
          : step === 'details'
          ? isPreselectedFlow
            ? 'Cancel'
            : 'Back'
          : undefined
      }
      buttonDisabled={loading || (!createdAwardIds.length && step === 'details' && !selectedType)}
      onButtonClick={
        createdAwardIds.length ? undefined : step === 'confirm-missing-houses' ? onConfirmMissingHouses : onSubmit
      }
      onCloseClick={step === 'confirm-missing-houses' ? onCancelMissingHouses : handleClose}
      onCloseIconClick={directClose}
      onBackdropClick={directClose}
    >
      {createdAwardIds.length ? (
        <div className="grantAwardSuccess">
          {errors.form ? <div className="grantAwardAlert is-error">{errors.form}</div> : null}

          <div className="grantAwardSuccess__summary">
            <span className="grantAwardSuccess__check" aria-hidden="true">
              ✓
            </span>
            <div>
              <strong>
                {createdAwardIds.length} {createdAwardIds.length === 1 ? 'award was' : 'awards were'} granted
              </strong>
              <p>
                {selectedType?.name || 'Award'} has been added for {recipientLabel}.
              </p>
            </div>
          </div>

          <div className="grantAwardSummaryChips">
            <span>{selectedType?.name || 'Award'}</span>
            <span>
              {new Date(`${awardedAt}T12:00:00`).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
            </span>
            <span className={points < 0 ? 'is-negative' : 'is-points'}>{points} pts each</span>
            {logs[0]?.performedBy?.displayName ? <span>By {logs[0].performedBy.displayName}</span> : null}
          </div>

          <div className="grantAwardMedia__header">
            <div>
              <strong>Photos and videos</strong>
              <span>Optional evidence for this award</span>
            </div>
            <div className="grantAwardMedia__actions">
              <button type="button" onClick={onAddPhotoClick} disabled={loading}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 7h3l1.5-2h7L17 7h3v12H4z" />
                  <circle cx="12" cy="13" r="3" />
                </svg>
                Add photo
              </button>
              <button type="button" onClick={onAddVideoClick} disabled={loading}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="3" y="6" width="13" height="12" rx="2" />
                  <path d="m16 10 5-3v10l-5-3z" />
                </svg>
                Add video
              </button>
            </div>
            <input ref={photoInputRef} type="file" accept="image/*" className="d-none" onChange={onPhotoFileChange} />
            <input ref={videoInputRef} type="file" accept="video/*" className="d-none" onChange={onVideoFileChange} />
          </div>

          {photos.length || videos.length ? (
            <div className="grantAwardMedia">
              {photos.length ? (
                <div className="grantAwardMedia__photos">
                  {photos.map((photo: any) => (
                    <div key={photo.id} className="grantAwardMedia__photo">
                      <img src={photo.picUrl} alt="Award evidence" />
                      <button
                        type="button"
                        onClick={() => onDeletePhoto(photo.id)}
                        disabled={loading}
                        aria-label="Delete photo"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
              {videos.length ? (
                <div className="grantAwardMedia__videos">
                  {videos.map((video: any) => (
                    <div key={video.id} className="grantAwardMedia__video">
                      <span className="grantAwardMedia__videoIcon" aria-hidden="true">
                        ▶
                      </span>
                      <span>
                        <strong>{video.fileName}</strong>
                        <small>{video.status || 'Processing'}</small>
                      </span>
                      <button
                        type="button"
                        onClick={() => onDeleteVideo(video.id)}
                        disabled={loading}
                        aria-label={`Delete ${video.fileName}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="grantAwardMedia__empty">No media added</div>
          )}
          {loading ? (
            <div className="grantAwardMedia__busy" aria-live="polite">
              Updating media…
            </div>
          ) : null}
        </div>
      ) : step === 'select' ? (
        <AwardTypeSelector
          types={types}
          loading={typesLoading}
          error={typesError}
          onSelect={type => {
            setSelectedType(type);
            setPoints(type.defaultHousePoints);
            setErrors({});
            setStep('details');
          }}
        />
      ) : step === 'confirm-missing-houses' ? (
        <div className="grantAwardWarning">
          <div className="grantAwardWarning__notice">
            <span aria-hidden="true">!</span>
            <div>
              <strong>House points cannot be applied to everyone</strong>
              <p>
                {!pendingWithHouse || pendingWithHouse.length === 0
                  ? 'The awards will still be created, but no house points will be recorded.'
                  : 'All selected students will receive the award. Students without a house will receive 0 house points.'}
              </p>
            </div>
          </div>

          <div className="grantAwardSummaryChips">
            <span className="is-points">{points} pts each</span>
            <span className="is-success">{pendingWithHouse?.length || 0} with a house</span>
            <span className="is-warning">{pendingNoHouse?.length || 0} without a house</span>
          </div>

          {pendingNoHouse && pendingNoHouse.length > 0 ? (
            <div className="grantAwardWarning__students">
              {pendingNoHouse.slice(0, 8).map(student => (
                <span key={student.id}>{`${student.firstName} ${student.lastName}`}</span>
              ))}
              {pendingNoHouse.length > 8 ? <span>+{pendingNoHouse.length - 8} more</span> : null}
            </div>
          ) : null}

          <p className="grantAwardWarning__hint">Houses can be assigned later from People / Students.</p>
        </div>
      ) : (
        <div className="grantAwardDetails">
          {errors.form ? <div className="grantAwardAlert is-error">{errors.form}</div> : null}

          {selectedType ? (
            <div className="grantAwardSelection">
              <span className="grantAwardSelection__visual">
                {selectedType.picture ? <img src={selectedType.picture} alt="" /> : <span aria-hidden="true">★</span>}
              </span>
              <span className="grantAwardSelection__copy">
                <small>Selected award</small>
                <strong>{selectedType.name}</strong>
                <span>{recipientLabel}</span>
              </span>
              <span className="grantAwardSelection__points">{points} pts</span>
              {!isPreselectedFlow ? (
                <button
                  type="button"
                  onClick={() => {
                    setStep('select');
                    setErrors({});
                  }}
                  disabled={loading}
                >
                  Change
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="grantAwardDetails__grid">
            <SitDatePicker
              label="Award date"
              mode="single"
              value={awardedAt ? new Date(`${awardedAt}T12:00:00`) : null}
              onChange={value => onAwardDateChange(value as Date)}
              format="DD MMM YYYY"
              placeholder="Select date"
              max={new Date()}
              centered={true}
              clearable={false}
              disabled={loading}
              className="grantAwardDetails__date"
            />

            <label className={`grantAwardField ${errors.points ? 'has-error' : ''}`}>
              <span>Points</span>
              <input
                type="number"
                value={Number.isFinite(points) ? points : 0}
                onChange={event => setPoints(Number(event.target.value))}
                disabled={loading}
              />
              {errors.points ? <small>{errors.points}</small> : null}
            </label>

            <label className="grantAwardField is-wide">
              <span>
                Reason <em>Optional</em>
              </span>
              <textarea
                value={reason}
                onChange={event => setReason(event.target.value)}
                disabled={loading}
                placeholder="Add a short reason"
                rows={3}
              />
            </label>

            <label className="grantAwardField is-wide">
              <span>
                Awarded by <em>Optional</em>
              </span>
              <input
                type="text"
                placeholder={profileName || 'Profile name will be used'}
                value={awardedByName}
                onChange={event => setAwardedByName(event.target.value)}
                disabled={loading}
              />
            </label>
          </div>
        </div>
      )}
    </SimpleModal>
  );
};
